import ApplicationController from "./application_controller"
import Logger from '../lib/logger'

export default class extends ApplicationController {
  static targets = ["content", "actions"]
  static values = {
    threshold: { type: Number, default: 50 },
    maxSwipe: { type: Number, default: 140 }
  }

  connect() {
    super.connect()
    this.startX = 0
    this.startY = 0
    this.currentX = 0
    this.isDragging = false
    this.state = 'closed' // 'closed', 'left', 'right'
    this.cspWarningLogged = false

    // Set class for max swipe distance to avoid inline styles (CSP compliance)
    if (this.hasContentTarget) {
      this.contentTarget.classList.add(`swipe-${this.maxSwipeValue}`)
    }

    // Close swiped actions when user scrolls
    this.addEventListener(window, 'scroll', this.closeOnScroll, { passive: true })

    // Close swiped actions when tapping outside
    this.addEventListener(document, 'touchstart', this.handleTapOutside, { passive: true })
  }

  disconnect() {
    super.disconnect()
  }

  handleTouchStart(event) {
    if (event.touches.length !== 1) return

    this.startX = event.touches[0].clientX
    this.startY = event.touches[0].clientY
    this.isDragging = true
    this.initialState = this.state

    Logger.debug('SwipeActions touchstart', { 
      startX: this.startX, 
      state: this.state 
    })
  }

  handleTouchMove(event) {
    if (!this.isDragging || !this.hasContentTarget) return

    const x = event.touches[0].clientX
    const y = event.touches[0].clientY
    const deltaX = x - this.startX
    const deltaY = y - this.startY

    // If vertical scrolling is more prominent, cancel swipe
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaX) < 10) {
      this.isDragging = false
      this.resetDragState()
      return
    }

    // Calculate current drag position based on initial state
    let moveX = deltaX
    if (this.initialState === 'left') {
      moveX = deltaX - this.maxSwipeValue
    } else if (this.initialState === 'right') {
      moveX = deltaX + this.maxSwipeValue
    }

    // Clamp movement
    if (moveX > this.maxSwipeValue) moveX = this.maxSwipeValue
    if (moveX < -this.maxSwipeValue) moveX = -this.maxSwipeValue

    this.currentX = moveX
    
    // Applying inline transform for smooth dragging. 
    // Fallback is the class-based snapping in handleTouchEnd.
    try {
      this.contentTarget.style.transform = `translateX(${moveX}px)`
    } catch (e) {
      if (!this.cspWarningLogged) {
        Logger.warn('SwipeActions: Inline transform blocked by CSP. Falling back to snapping.', { error: e.message })
        this.cspWarningLogged = true
      }
    }

    // Prevent default to stop scrolling horizontally if swiping
    if (Math.abs(deltaX) > 10) {
      event.preventDefault()
    }
  }

  handleTouchEnd() {
    if (!this.isDragging || !this.hasContentTarget) return
    this.isDragging = false

    Logger.debug('SwipeActions touchend', { 
      currentX: this.currentX, 
      threshold: this.thresholdValue 
    })

    // Clear inline transform to let CSS classes take over
    try {
      this.contentTarget.style.transform = ''
    } catch (e) {}

    // Determine final state
    if (this.currentX > this.thresholdValue) {
      this.openRight()
    } else if (this.currentX < -this.thresholdValue) {
      this.openLeft()
    } else {
      this.close()
    }

    this.currentX = 0
  }

  openLeft() {
    if (!this.hasContentTarget) return
    this.contentTarget.classList.add('swiped-left')
    this.contentTarget.classList.remove('swiped-right')
    this.state = 'left'
    Logger.success('SwipeActions opened left (revealed right actions)')
  }

  openRight() {
    if (!this.hasContentTarget) return
    this.contentTarget.classList.add('swiped-right')
    this.contentTarget.classList.remove('swiped-left')
    this.state = 'right'
    Logger.success('SwipeActions opened right (revealed left actions)')
  }

  close() {
    if (!this.hasContentTarget) return
    this.contentTarget.classList.remove('swiped-left', 'swiped-right')
    this.state = 'closed'
    Logger.debug('SwipeActions closed')
  }

  // Reset drag state without changing open/close position
  resetDragState() {
    if (this.hasContentTarget) {
      try {
        this.contentTarget.style.transform = ''
      } catch (e) {}
    }
    this.currentX = 0
  }

  closeOnScroll() {
    if (this.state !== 'closed') {
      Logger.debug('SwipeActions closing on scroll')
      this.close()
    }
  }

  handleTapOutside(event) {
    if (this.state === 'closed') return
    if (!this.element.contains(event.target)) {
      Logger.debug('SwipeActions closing on tap outside')
      this.close()
    }
  }
}
